import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form } from 'coreOld/components';
import { InputType } from 'coreOld/enums';
import { companiesService } from 'partsOld/services';
import { UsersOptions } from 'system/constants';
import { useGetRanks, useUserRanksActions, useUsersActions } from 'system/hooks';
import {
  mapUserChangeAssignedCompanyDataToSend,
  mapUserRanksDataToSend,
  mapUserSettingsDataToSend,
} from 'system/mappers';
import { ISystemUser, IUserForm } from 'system/types';

import { editUserBulkSchema } from './usersSchema';

interface IProps {
  selectedRows: ISystemUser[];
  selectedOption: UsersOptions;
  setSelectedOption?: Dispatch<SetStateAction<UsersOptions>>;
  setCloseSelection?: Dispatch<SetStateAction<boolean>>;
}

const UsersForm = ({
  selectedRows,
  selectedOption,
  setSelectedOption,
  setCloseSelection,
}: IProps) => {
  const { t } = useTranslation();

  const { editBulk, editUserSettingsBulk } = useUsersActions();
  const { addUserRanksBulk, removeUserRanksBulk } = useUserRanksActions();

  const getFormTitle = () => {
    switch (selectedOption) {
      case UsersOptions.CHANGE_ASSIGNED_COMPANY:
        return t('labelChangeAssignedCompany');
      case UsersOptions.ADD_RANK:
        return t('labelUpdateUserRanks');
      case UsersOptions.ALLOW_QUOTE_RELEASE_WITHOUT_SENDING_EMAIL:
        return t('labelUpdateUserSettings');
      default:
        return '';
    }
  };

  const onSubmit = (reset) => async (data: IUserForm) => {
    const userRankAssociations = mapUserRanksDataToSend(selectedRows, data?.ranks);

    switch (selectedOption) {
      case UsersOptions.CHANGE_ASSIGNED_COMPANY:
        await editBulk(
          mapUserChangeAssignedCompanyDataToSend(selectedRows, data?.assignedCompany?.id)
        );

        break;
      case UsersOptions.ADD_RANK:
        await addUserRanksBulk(userRankAssociations);

        break;
      case UsersOptions.REMOVE_RANK:
        await removeUserRanksBulk(userRankAssociations);

        break;
      case UsersOptions.ALLOW_QUOTE_RELEASE_WITHOUT_SENDING_EMAIL:
        await editUserSettingsBulk(
          mapUserSettingsDataToSend(
            selectedRows,
            data?.allowQuoteReleaseWithoutSendingEmail ?? true
          )
        );

        break;
    }

    reset({ assignedCompany: null, ranks: null });
    setSelectedOption(null);
    setCloseSelection(true);
  };

  const onClose = (reset) => {
    setSelectedOption(null);
    reset({ assignedCompany: null, ranks: null });
  };

  const renderInput = () => {
    switch (selectedOption) {
      case UsersOptions.CHANGE_ASSIGNED_COMPANY:
        return (
          <Field
            name="assignedCompany"
            type={InputType.ASYNC_SELECT}
            label={t('labelAssignedCompany')}
            inputProps={{
              getOptions: companiesService.getManyAutocomplete,
              getValue: (opt) => opt,
              getLabel: (opt) => (opt ? `${opt?.name} [${opt?.id}]` : ''),
              showDropdownIcon: true,
              isClearable: false,
            }}
            required
          />
        );
      case UsersOptions.ADD_RANK:
        return (
          <Field
            name="ranks"
            type={InputType.SELECT}
            label="Ranks to be added"
            inputProps={{
              useGetOptions: useGetRanks,
              getValue: (opt) => opt?.rankId,
              getLabel: (opt) => opt?.rankName,
              placeholder: 'Ranks',
              isMulti: true,
              showDropdownIcon: false,
            }}
            required
          />
        );
      case UsersOptions.REMOVE_RANK:
        return (
          <Field
            name="ranks"
            type={InputType.SELECT}
            label="Ranks to be removed"
            inputProps={{
              useGetOptions: useGetRanks,
              getValue: (opt) => opt?.rankId,
              getLabel: (opt) => opt?.rankName,
              placeholder: 'Ranks',
              isMulti: true,
              showDropdownIcon: false,
            }}
            required
          />
        );
      case UsersOptions.ALLOW_QUOTE_RELEASE_WITHOUT_SENDING_EMAIL:
        return (
          <Field
            name="allowQuoteReleaseWithoutSendingEmail"
            type={InputType.TOGGLE_SWITCH}
            label={t('messageAllowQuoteReleaseWithoutSendingEmail')}
            inputProps={{
              defaultChecked: true,
              value: true,
            }}
          />
        );
    }
  };

  return (
    <Form<IUserForm>
      isOpen={Boolean(selectedOption)}
      onClose={onClose}
      title={getFormTitle()}
      defaultValues={{
        assignedCompany: null,
        ranks: null,
        allowQuoteReleaseWithoutSendingEmail: true,
      }}
      submitBtnLabel={t('buttonLabelUpdate')}
      onSubmit={onSubmit}
      schema={editUserBulkSchema(selectedOption)}
    >
      {renderInput()}
    </Form>
  );
};

export default UsersForm;
